/**
 * Iter 67bj — Public 2-month price calendar on the car detail page.
 *
 * Shows two side-by-side month grids with the effective €/day on each cell,
 * colour-coded by source:
 *   · base     — neutral
 *   · seasonal — soft blue (vendor's vendor-wide multiplier for that month)
 *   · rule     — violet (per-car date-range override; takes priority)
 *
 * Backend: GET /api/cars/{car_id}/daily-rates?start=&end=
 * Window: today → today + 62 days (≈ 2 months of cells, the strip on the
 * Date-range editor mirrors the same length).
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";

const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const WEEKDAYS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

function _ymd(d) {
  return d.toISOString().slice(0, 10);
}

function _firstOfMonth(year, month) {
  return new Date(year, month, 1);
}

// Returns the (Monday-first) weekday index 0..6 for `date`.
function _weekdayIdxMonFirst(date) {
  const d = date.getDay(); // 0 = Sun, 1 = Mon, ...
  return (d + 6) % 7;
}

function _daysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function MonthGrid({ year, month, ratesByDate, today, basePrice }) {
  const firstDay = _firstOfMonth(year, month);
  const offset = _weekdayIdxMonFirst(firstDay);
  const total = _daysInMonth(year, month);
  const todayISO = _ymd(today);

  const cells = [];
  for (let i = 0; i < offset; i++) cells.push({ blank: true, key: `b-${i}` });
  for (let d = 1; d <= total; d++) {
    const dt = new Date(year, month, d);
    const iso = _ymd(dt);
    const r = ratesByDate[iso];
    cells.push({
      blank: false,
      key: iso,
      day: d,
      iso,
      isToday: iso === todayISO,
      isPast: iso < todayISO,
      rate: r,
    });
  }

  return (
    <div className="flex-1 min-w-0" data-testid={`price-cal-${year}-${month + 1}`}>
      <div className="text-sm font-semibold text-neutral-900 mb-3">{MONTH_NAMES[month]} {year}</div>
      <div className="grid grid-cols-7 gap-1 text-[9px] uppercase tracking-wider font-bold text-neutral-500 mb-1">
        {WEEKDAYS.map((w) => <div key={w} className="text-center">{w}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((c) => {
          if (c.blank) return <div key={c.key} className="aspect-square" />;
          const isUnavailable = c.rate && c.rate.available === false;
          let tone = "bg-neutral-50 text-neutral-900";
          if (c.rate?.source === "rule") {
            tone = "bg-violet-100 text-violet-900 ring-1 ring-violet-200";
          }
          // Iter 67bk — seasonal-multiplier days now render with the base tone
          // (the "Seasonal" concept is no longer surfaced as a separate
          // visual treatment). The price label still reflects the multiplied
          // amount because the backend already applied it.
          if (c.isPast) tone = "bg-neutral-50 text-neutral-300";
          // Iter 67bl — booked days override every other tone with a striped
          // rose treatment so renters spot them at a glance.
          if (isUnavailable && !c.isPast) {
            tone = "bg-rose-50 text-rose-700 ring-1 ring-rose-200 [background-image:repeating-linear-gradient(45deg,transparent_0_3px,rgba(244,63,94,0.18)_3px_6px)] line-through decoration-rose-400/70";
          }
          const todayClass = c.isToday ? "ring-2 ring-offset-1 ring-amber-500" : "";
          const priceLabel = c.rate ? `€${c.rate.price_eur.toFixed(0)}` : (basePrice != null ? `€${Number(basePrice).toFixed(0)}` : "—");
          const ruleName = c.rate?.rule_name;
          const titleParts = [c.iso];
          if (c.rate) {
            titleParts.push(`€${c.rate.price_eur.toFixed(2)}/day`);
            if (c.rate.source === "rule" && ruleName) titleParts.push(ruleName);
            else titleParts.push("daily rate");
            if (isUnavailable) titleParts.push("Unavailable (booked)");
          }
          return (
            <div
              key={c.key}
              className={`aspect-square rounded-md px-1 py-1 flex flex-col items-center justify-center text-center transition ${tone} ${todayClass}`}
              title={titleParts.join(" · ")}
              data-testid={`price-cal-day-${c.iso}`}
              data-available={c.rate ? (c.rate.available === false ? "false" : "true") : undefined}
            >
              <div className="text-[10px] font-semibold leading-none">{c.day}</div>
              <div className={`text-[10px] font-bold leading-none mt-1 tabular-nums ${c.isPast ? "" : "opacity-90"}`}>{priceLabel}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function CarPriceCalendar({ carId }) {
  // Anchor month = current month; user can flip ± to look ahead or back.
  const today = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);
  const [anchor, setAnchor] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
  const [rates, setRates] = useState(null);
  const [basePrice, setBasePrice] = useState(null);
  const [loading, setLoading] = useState(true);

  const monthA = anchor;
  const monthB = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1);
  const windowStart = _ymd(monthA);
  const windowEnd = _ymd(new Date(monthB.getFullYear(), monthB.getMonth() + 1, 1)); // first of (B+1)

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/cars/${carId}/daily-rates`, { params: { start: windowStart, end: windowEnd } })
      .then((r) => {
        if (!alive) return;
        const map = {};
        for (const d of r.data.days || []) map[d.date] = d;
        setRates(map);
        setBasePrice(r.data.base_price_eur);
      })
      .catch(() => { if (alive) setRates({}); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [carId, windowStart, windowEnd]);

  // Flip by 1 month so monthA always advances/retreats by exactly one column.
  const flip = (delta) => {
    setAnchor((a) => new Date(a.getFullYear(), a.getMonth() + delta, 1));
  };
  const minAnchor = new Date(today.getFullYear(), today.getMonth(), 1);
  const canFlipBack = anchor > minAnchor;
  // Don't let users wander too far forward — backend caps the window anyway.
  const maxAnchor = new Date(today.getFullYear(), today.getMonth() + 4, 1);
  const canFlipForward = anchor < maxAnchor;

  return (
    <div className="rounded-2xl border border-border bg-white p-5 shadow-sm" data-testid="car-price-calendar">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-violet-700">Daily rates</div>
          <div className="font-semibold text-neutral-900 text-sm">Price per day · next 2 months</div>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => flip(-1)}
            disabled={!canFlipBack || loading}
            className="h-8 w-8 rounded-full bg-neutral-100 hover:bg-neutral-200 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center transition"
            data-testid="price-cal-prev"
            aria-label="Previous month"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => flip(1)}
            disabled={!canFlipForward || loading}
            className="h-8 w-8 rounded-full bg-neutral-100 hover:bg-neutral-200 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center transition"
            data-testid="price-cal-next"
            aria-label="Next month"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {loading || !rates ? (
        <div className="text-center py-10 text-neutral-500 text-sm"><Loader2 className="h-4 w-4 animate-spin inline mr-1" /> Loading rates…</div>
      ) : (
        <div className="flex flex-col md:flex-row gap-6">
          <MonthGrid year={monthA.getFullYear()} month={monthA.getMonth()} ratesByDate={rates} today={today} basePrice={basePrice} />
          <MonthGrid year={monthB.getFullYear()} month={monthB.getMonth()} ratesByDate={rates} today={today} basePrice={basePrice} />
        </div>
      )}

      <div className="flex flex-wrap gap-3 mt-4 text-[11px] text-neutral-700" data-testid="price-cal-legend">
        <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-neutral-50 ring-1 ring-neutral-200" /> Base rate</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-violet-100 ring-1 ring-violet-200" /> Custom date-range</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-rose-50 ring-1 ring-rose-200 [background-image:repeating-linear-gradient(45deg,transparent_0_2px,rgba(244,63,94,0.25)_2px_4px)]" /> Unavailable</span>
      </div>
    </div>
  );
}
